import {
  addDays,
  isAfter,
  isBefore,
  isSameDay,
  startOfDay,
  startOfWeek,
} from 'date-fns';
import { Pressable, StyleSheet, View } from 'react-native';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import theme from 'shared/styles/theme';

const CALENDAR_GRID_TOTAL_DAYS = 42;
const DAYS_PER_WEEK = 7;
const DEFAULT_LOCALE = 'en-US';

export interface LocalizedWeekday {
  id: number;
  label: string;
}

export const getLocalizedWeekdayHeaders = (
  locale: string = DEFAULT_LOCALE,
): LocalizedWeekday[] => {
  const weekdayFormatter = new Intl.DateTimeFormat(locale, {
    weekday: 'narrow',
  });
  return Array.from({ length: DAYS_PER_WEEK }, (_, dayIndex) => {
    return {
      id: dayIndex,
      label: weekdayFormatter.format(new Date(2024, 0, 7 + dayIndex)),
    };
  });
};

export const getCalendarGridDays = (
  viewYear: number,
  viewMonth: number,
): Date[] => {
  const firstDayOfMonth = new Date(viewYear, viewMonth, 1);
  const calendarGridStartDate = startOfWeek(firstDayOfMonth);
  return Array.from({ length: CALENDAR_GRID_TOTAL_DAYS }, (_, dayOffset) => {
    return addDays(calendarGridStartDate, dayOffset);
  });
};

const isDateDisabled = (
  targetDate: Date,
  minimumDate?: Date,
  maximumDate?: Date,
): boolean => {
  const normalizedTargetDate = startOfDay(targetDate);
  if (minimumDate && isBefore(normalizedTargetDate, startOfDay(minimumDate))) {
    return true;
  }
  if (maximumDate && isAfter(normalizedTargetDate, startOfDay(maximumDate))) {
    return true;
  }
  return false;
};

interface DatePickerCalendarGridProps {
  weekdayHeaders: LocalizedWeekday[];
  days: Date[];
  activeViewMonth: number;
  selectedDate?: Date;
  minimumDate?: Date;
  maximumDate?: Date;
  locale: string;
  onSelectDay: (date: Date) => void;
}

const DatePickerCalendarGrid = ({
  weekdayHeaders,
  days,
  activeViewMonth,
  selectedDate,
  minimumDate,
  maximumDate,
  locale,
  onSelectDay,
}: DatePickerCalendarGridProps) => {
  const dayAriaLabelFormatter = new Intl.DateTimeFormat(locale, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <>
      <View style={styles.weekdayRow}>
        {weekdayHeaders.map((weekday) => (
          <View key={weekday.id} style={styles.weekdayCell}>
            <BodySmText style={styles.weekdayText}>{weekday.label}</BodySmText>
          </View>
        ))}
      </View>

      <View style={styles.daysGrid}>
        {days.map((calendarDayDate) => {
          const isDateSelected = Boolean(
            selectedDate && isSameDay(calendarDayDate, selectedDate),
          );
          const isDateInCurrentMonth =
            calendarDayDate.getMonth() === activeViewMonth;
          const isCellDisabled = isDateDisabled(
            calendarDayDate,
            minimumDate,
            maximumDate,
          );
          const dayCellAriaLabel =
            dayAriaLabelFormatter.format(calendarDayDate);

          return (
            <Pressable
              key={calendarDayDate.toISOString()}
              disabled={isCellDisabled}
              onPress={() => {
                onSelectDay(calendarDayDate);
              }}
              accessibilityRole="button"
              accessibilityLabel={dayCellAriaLabel}
              accessibilityState={{
                selected: isDateSelected,
                disabled: isCellDisabled,
              }}
              style={({ hovered }) => [
                styles.dayCell,
                isCellDisabled && styles.disabled,
                isDateSelected && styles.dayCellSelected,
                hovered &&
                  !isDateSelected &&
                  !isCellDisabled &&
                  styles.dayCellHovered,
              ]}
            >
              {({ hovered }) => (
                <BodyBaseText
                  style={[
                    styles.dayText,
                    !isDateInCurrentMonth && styles.dayTextOutsideMonth,
                    isDateSelected && styles.dayTextSelected,
                    hovered &&
                      !isCellDisabled &&
                      !isDateSelected &&
                      styles.dayTextHovered,
                  ]}
                >
                  {calendarDayDate.getDate()}
                </BodyBaseText>
              )}
            </Pressable>
          );
        })}
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  weekdayRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: theme.spacing.xs,
    zIndex: 1,
  },
  weekdayCell: { width: 36, alignItems: 'center' },
  weekdayText: {
    fontSize: 12,
    fontWeight: '600',
    color: theme.color.text.secondary,
  },
  daysGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    rowGap: 2,
    zIndex: 1,
  },
  dayCell: {
    height: 36,
    width: 36,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 10,
  },
  dayCellSelected: { backgroundColor: theme.color.interactive.primary },
  dayCellHovered: { backgroundColor: theme.color.border.focusedInverse },
  dayText: { fontSize: 14, color: theme.color.text.primary },
  dayTextOutsideMonth: { color: theme.color.text.muted },
  dayTextSelected: { color: theme.color.text.inverse, fontWeight: '600' },
  dayTextHovered: { color: theme.color.interactive.primary },
  disabled: { opacity: 0.3 },
});

export default DatePickerCalendarGrid;
